/**
 * Musician Configuration Hook
 * 
 * Provides musician-specific configuration including icon sets and display names.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMusicianConfig } from '../config/musicians';

/**
 * Musician Context
 */
const MusicianContext = createContext(null);

/**
 * Musician Context Provider Component
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.defaultMusicianId - Default musician ID
 */
export function MusicianProvider({ children, defaultMusicianId = null }) {
  const [musicianId, setMusicianId] = useState(defaultMusicianId);
  const [config, setConfig] = useState(getMusicianConfig(defaultMusicianId));
  
  /**
   * Update musician configuration when ID changes
   */
  useEffect(() => {
    const newConfig = getMusicianConfig(musicianId);
    setConfig(newConfig);
  }, [musicianId]);
  
  /**
   * Update musician by ID
   */
  const updateMusicianById = useCallback((id) => {
    setMusicianId(id);
  }, []);
  
  const value = {
    musicianId,
    musicianName: config.name,
    musicianIcons: config.icons,
    updateMusicianById,
  };
  
  return (
    <MusicianContext.Provider value={value}>
      {children}
    </MusicianContext.Provider>
  );
}

/**
 * Hook to access musician configuration
 * 
 * @returns {object} Musician configuration
 * @throws {Error} If used outside MusicianProvider
 */
export function useMusicianConfig() {
  const context = useContext(MusicianContext);
  
  if (!context) {
    throw new Error('useMusicianConfig must be used within MusicianProvider');
  }
  
  return context;
}

export default MusicianContext;


